import { useQuery } from '@apollo/client';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import { formatGraphqlProductOnwership } from 'bundles/enroll/utils/entitlementProductOwnershipUtils';
import type { FormattedGraphqlProductOwnership } from 'bundles/enroll/utils/entitlementProductOwnershipUtils';
import GetProductOwnership from 'bundles/enroll/utils/queries/GetProductOwnershipQuery.graphql';
import type {
  GetProductOwnershipQuery,
  GetProductOwnershipQueryVariables,
} from 'bundles/enroll/utils/queries/__generated__/GetProductOwnershipQuery';
import paymentsExperiments from 'bundles/epic/clients/payments';
import { useNaptime } from 'bundles/naptimejs';
import ProductOwnershipsV1 from 'bundles/naptimejs/resources/productOwnerships.v1';
import { SPECIALIZATION, VERIFIED_CERTIFICATE } from 'bundles/payments/common/ProductType';

type NaptimeProps = {
  productOwnerships?: ProductOwnershipsV1;
};

export type ProductOwnershipsReturnType = {
  courseOwnership?: FormattedGraphqlProductOwnership;
  s12nOwnership?: FormattedGraphqlProductOwnership;
};

export const useGetProductOwnerships = (productOwnershipsId: string | undefined) => {
  const isUsingDGSProductOwnership = paymentsExperiments.get('useDGSProductOwnership');

  const { data } = useQuery<GetProductOwnershipQuery, GetProductOwnershipQueryVariables>(GetProductOwnership, {
    skip: !isUsingDGSProductOwnership || !productOwnershipsId,
    variables: {
      productOwnershipId: productOwnershipsId || '',
    },
    context: { clientName: 'gatewayGql' },
  });

  const naptimeResponse = useNaptime<NaptimeProps>(() => {
    if (isUsingDGSProductOwnership || !productOwnershipsId) {
      return {};
    }

    return {
      productOwnerships: ProductOwnershipsV1.get(productOwnershipsId, {
        fields: ['s12nCourseOwnerships', 'owns'],
      }),
    };
  }, [productOwnershipsId]);

  return isUsingDGSProductOwnership
    ? formatGraphqlProductOnwership(data?.Entitlement?.queryProductOwnership)
    : naptimeResponse.data.productOwnerships;
};

/**
 * Custom hook to replace `withProductOwnerships` HOC.
 * @param {string} courseId
 * @param {string} s12nId
 * @returns {ProductOwnershipsReturnType}
 */

const useProductOwnerships = ({
  courseId,
  s12nId,
}: {
  courseId?: string;
  s12nId?: string;
}): ProductOwnershipsReturnType => {
  const courseProductOwnershipsId =
    user.isAuthenticatedUser() && courseId
      ? tupleToStringKey([user.get().id, VERIFIED_CERTIFICATE, courseId])
      : undefined;
  const s12nProductOwnershipsId =
    user.isAuthenticatedUser() && s12nId ? tupleToStringKey([user.get().id, SPECIALIZATION, s12nId]) : undefined;

  const courseOwnership = useGetProductOwnerships(courseProductOwnershipsId);
  const s12nOwnership = useGetProductOwnerships(s12nProductOwnershipsId);

  return {
    courseOwnership,
    s12nOwnership,
  };
};

export default useProductOwnerships;
